const focusableSelector =
  ':is(input:not([type="hidden"]), a[href], button:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])):not(#player *), #player';

/**
 * Traps keyboard focus within the given element.
 * Attaches a `keydown` listener on `document` and returns a cleanup function
 * that removes the listener when called.
 *
 * @param {HTMLElement | null} element - The DOM element to trap focus within.
 * @returns {() => void} Cleanup function that removes the event listener.
 */
export function trapFocus(element) {
  if (!element) return () => {};
  const handleKeyDown = (e) => {
    if (e.key !== 'Tab') return;

    const focusableElements = element.querySelectorAll(focusableSelector);

    if (focusableElements.length === 0) {
      e.preventDefault(); // keep focus inside (nothing to Tab to)
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    // If focus escaped the element (e.g. during paint or programmatic blur),
    // bring it back rather than letting Tab roam the page.
    if (!element.contains(document.activeElement)) {
      e.preventDefault();
      if (e.shiftKey) {
        lastElement.focus();
      } else {
        firstElement.focus();
      }
      return;
    }

    if (e.shiftKey) {
      /* shift + tab */
      if (document.activeElement === firstElement) {
        lastElement.focus();
        e.preventDefault();
      }
    } else if (document.activeElement === lastElement) {
      /* tab */
      firstElement.focus();
      e.preventDefault();
    }
  };

  document.addEventListener('keydown', handleKeyDown);

  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
}

